import React, { useEffect, useState } from "react";
import Property from "../components/hostComponents/Property";
import HostNav from "../components/hostComponents/HostNav";
import { useDispatch, useSelector } from "react-redux";
import { getFlags, getHostProperties, getHostEarnings } from "../store/thunks/hostActions";
import { useNavigate } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import {
  updatePropertyReset,
  deletePropertyReset,
  addPropertyReset,
} from "../store/slices/hostSlices";
import request from "../utils/request";
import onboard from "../icons/onboard.svg";
import TutorialModal from "../components/TutorialModal";
import Calendly from "./Calendly";
import { Link } from "react-router-dom";
import { isUpcomingOrder } from "../utils/orderStatus";

// ─── Compact Analytics Bar ───────────────────────────────────────────────────
function AnalyticsBar({ activeProperties, totalEarnings, totalOrders, avgOrderValue }) {
  const navigate = useNavigate();

  const stats = [
    {
      label: 'Properties',
      value: activeProperties,
      gradient: 'from-blue-500 to-indigo-600',
      shadow: 'shadow-blue-500/20',
      onClick: () => navigate('/properties'),
    },
    {
      label: 'Earnings',
      value: `$${totalEarnings.toFixed(2)}`,
      gradient: 'from-green-500 to-emerald-600',
      shadow: 'shadow-green-500/20',
      onClick: () => navigate('/earnings'),
    },
    {
      label: 'Orders',
      value: totalOrders,
      gradient: 'from-purple-500 to-violet-600',
      shadow: 'shadow-purple-500/20',
      onClick: () => navigate('/order-list'),
    },
    {
      label: 'Avg Value',
      value: `$${avgOrderValue.toFixed(2)}`,
      gradient: 'from-amber-500 to-orange-600',
      shadow: 'shadow-amber-500/20',
      onClick: () => navigate('/earnings'),
    },
  ];

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 p-3 mb-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((stat, i) => (
          <button
            key={i}
            onClick={stat.onClick}
            className="group flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-gray-50/60 transition-all duration-200 text-left"
          >
            <div className={`w-9 h-9 bg-gradient-to-br ${stat.gradient} rounded-lg flex items-center justify-center shadow-md ${stat.shadow} group-hover:scale-110 transition-transform duration-200 flex-shrink-0`}>
              <span className="text-white text-xs font-bold">{typeof stat.value === 'number' ? stat.value : '$'}</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{stat.label}</p>
              <p className="text-sm font-bold text-gray-900 truncate">{stat.value}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Property List Row (compact list view) ───────────────────────────────────
function PropertyListRow({ property, data, rawDataList, earnings }) {
  const navigate = useNavigate();

  const propertyEarnings = earnings?.[property._id]?.earnings?.reduce((sum, amt) => sum + amt, 0) || 0;
  const orderCount = earnings?.[property._id]?.earnings?.length || 0;

  const formatAddress = (addr) => {
    if (!addr) return 'N/A';
    return [addr.line1, addr.city, addr.state].filter(Boolean).join(', ');
  };

  return (
    <div
      onClick={() => navigate(`/properties/${property._id}`)}
      className="group flex items-center justify-between px-5 py-3.5 bg-white/70 backdrop-blur-xl border border-white/40 rounded-xl hover:shadow-md hover:bg-white/90 transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-center space-x-4 min-w-0 flex-1">
        {/* Icon */}
        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/20 flex-shrink-0 group-hover:scale-105 transition-transform">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>

        {/* Details */}
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">
            {property.listingTitle || 'Untitled Property'}
          </p>
          <p className="text-xs text-gray-500 truncate">{formatAddress(property.address)}</p>
        </div>
      </div>

      {/* Metrics */}
      <div className="flex items-center space-x-6 flex-shrink-0">
        <div className="hidden sm:block text-right">
          <p className="text-xs text-gray-400 font-medium">Orders</p>
          <p className="text-sm font-bold text-gray-900">{orderCount}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400 font-medium">Earned</p>
          <p className="text-sm font-bold text-green-600">${propertyEarnings.toFixed(2)}</p>
        </div>
        <div className="flex items-center space-x-1.5">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs font-medium text-gray-500 hidden md:inline">Active</span>
        </div>
        <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

// ─── Upcoming Orders Widget ────────────────────────────────────────────────────
function UpcomingOrdersWidget({ orders = [], receiveEntryInstructionsEmails = false }) {
  const navigate = useNavigate();
  const safeOrders = Array.isArray(orders) ? orders : [];
  
  // Future orders in active/pre-delivery statuses
  const upcomingOrders = safeOrders.filter((order) => isUpcomingOrder(order));

  // Count orders needing details (if host has the flag enabled)
  const ordersNeedingDetails = receiveEntryInstructionsEmails 
    ? upcomingOrders.filter(order => !order.hostOrderDetailsInputted).length 
    : 0;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (upcomingOrders.length === 0) {
    return (
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/40">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-bold text-gray-900">Upcoming Orders</h3>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">0</span>
          </div>
          <Link to="/order-list" className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors">View All</Link>
        </div>
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md shadow-amber-400/20">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">No Upcoming Orders</h4>
          <p className="text-xs text-gray-500 mb-3">Pending orders will appear here</p>
          <div className="inline-flex items-center px-3 py-1.5 bg-amber-50 rounded-full">
            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse mr-1.5"></div>
            <span className="text-xs font-medium text-amber-600">Waiting for orders...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/40">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-bold text-gray-900">Upcoming Orders</h3>
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">{upcomingOrders.length}</span>
        </div>
        <Link to="/order-list" className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors">View All</Link>
      </div>

      {/* Alert banner if orders need entry instructions */}
      {receiveEntryInstructionsEmails && ordersNeedingDetails > 0 && (
        <div className="mb-3 p-2.5 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200/50">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-red-700">
              <span className="font-bold">{ordersNeedingDetails}</span> order{ordersNeedingDetails > 1 ? 's need' : ' needs'} entry instructions & delivery time
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {upcomingOrders.slice(0, 5).map((order, index) => {
          const needsDetails = receiveEntryInstructionsEmails && !order.hostOrderDetailsInputted;
          
          return (
            <div
              key={order._id || index}
              className={`flex items-center space-x-3 p-2.5 rounded-xl transition-colors cursor-pointer border ${
                needsDetails 
                  ? 'bg-gradient-to-r from-red-50/70 to-orange-50/70 border-red-200/60 hover:from-red-50 hover:to-orange-50' 
                  : 'bg-gradient-to-r from-amber-50/50 to-orange-50/50 border-amber-100/50 hover:from-amber-50 hover:to-orange-50'
              }`}
              onClick={() => navigate('/order-list')}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 ${
                needsDetails 
                  ? 'bg-gradient-to-br from-red-500 to-orange-500' 
                  : 'bg-gradient-to-br from-amber-500 to-orange-500'
              }`}>
                {needsDetails ? (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-1.5">
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
                  </p>
                  {needsDetails && (
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[9px] font-bold rounded uppercase">
                      Needs Info
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {order.property?.listingTitle || order.property?.address?.city || 'Property'}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-xs font-bold ${needsDetails ? 'text-red-600' : 'text-amber-600'}`}>
                  ${order.hostFee?.toFixed(2) || '0.00'}
                </p>
                <p className="text-[10px] text-gray-400">{formatDate(order.inStockDate || order.createdAt)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Quick Actions Widget ────────────────────────────────────────────────────
function QuickActionsWidget() {
  const navigate = useNavigate();
  const { hostInfo } = useSelector((state) => state.host);

  const actions = [
    {
      title: "Add Property",
      description: "List a new property",
      icon: "M12 6v6m0 0v6m0-6h6m-6 0H6",
      gradient: "from-primary-500 to-primary-600",
      shadow: "shadow-primary-500/25",
      link: "/add-property"
    },
    {
      title: "View Orders",
      description: "Check recent orders",
      icon: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z",
      gradient: "from-green-500 to-emerald-600",
      shadow: "shadow-green-500/25",
      link: "/order-list"
    },
    {
      title: "Earnings",
      description: "Track performance",
      icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1",
      gradient: "from-emerald-500 to-green-600",
      shadow: "shadow-emerald-500/25",
      link: "/earnings"
    },
    {
      title: "Settings",
      description: "Account & profile",
      icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
      gradient: "from-purple-500 to-violet-600",
      shadow: "shadow-purple-500/25",
      link: `/settings/${hostInfo?._id}`
    }
  ];

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-sm border border-white/40">
      <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-2">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.link)}
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${action.gradient} p-3 text-white shadow-md ${action.shadow} hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 rounded-xl"></div>
            <div className="relative z-10">
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-200 border border-white/20">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
                </svg>
              </div>
              <h4 className="font-bold text-xs mb-0.5">{action.title}</h4>
              <p className="text-white/70 text-[10px]">{action.description}</p>
            </div>
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── View Toggle ─────────────────────────────────────────────────────────────
function ViewToggle({ viewMode, setViewMode }) {
  return (
    <div className="flex items-center bg-gray-100/60 backdrop-blur-sm rounded-xl p-1 border border-gray-200/40">
      <button
        onClick={() => setViewMode('grid')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
      <button
        onClick={() => setViewMode('list')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
    </div>
  );
}

// ─── Main Host Screen ────────────────────────────────────────────────────────
const HostScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [onBoardLoading, setOnBoardLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [errorOnBorad, setErrorOnBoard] = useState();
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(true);
  const [rawDataList, setRawDataList] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [viewMode, setViewMode] = useState("grid");

  const handleClick = async () => {
    setOnBoardLoading(true);
    try {
      const { data } = await request.get("/stripe/get-onboarding-link");
      window.location.href = data.url;
    } catch (error) {
      setErrorOnBoard(error.response.data.msg);
    }
  };

  const { hostInfo } = useSelector((state) => state.host);
  const hostDetails = useSelector((state) => state.hostDetails.Info);
  const isOnBoarded = hostDetails?.isOnBoarded;
  const isOnboardingBooked = hostDetails?.isOnboardingBooked;
  const selfOnboarding = hostDetails?.selfOnboarding;

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!hostInfo?._id) return;

    const fetchData = async () => {
      try {
        const response = await request.get(
          `properties/host/${hostInfo._id}/commission`
        );
        setData(response.data.commission);
      } catch (error) {
        console.error("An error occurred:", error);
        setData([]);
      }
    };

    const fetchRawData = async () => {
      try {
        const response = await request.get(`/rawdata/host/${hostInfo._id}`, {
          withCredentials: true,
        });
        setRawDataList(response.data || []);
      } catch (error) {
        console.error("Error fetching raw data:", error);
        setRawDataList([]);
      }
    };

    const fetchOrders = async () => {
      try {
        const response = await request.get(`/orders/host/${hostInfo._id}`, {
          withCredentials: true,
        });
        // API returns { orders: [...] } structure
        setAllOrders(response.data?.orders || []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setAllOrders([]);
      }
    };

    if (hostInfo._id) {
      setIsLoading(true);
      dispatch(getFlags(hostInfo._id));
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      fetchData();
      fetchRawData();
      fetchOrders();
      setIsLoading(false);
    }
  }, [hostInfo?._id, dispatch]);

  const { loading: loadingProperties, properties, error } = useSelector(
    (state) => state.hostProperties
  );

  const { earnings } = useSelector((state) => state.hostEarnings);
  
  const { receiveEntryInstructionsEmails } = useSelector((state) => state.hostFlags);

  const { success: successUpdate, message: messsageupdate } = useSelector(
    (state) => state.propertyUpdate
  );
  const { success: successDelete, message: messageDelete } = useSelector(
    (state) => state.propertyDelete
  );
  const {
    success: successAddProperty,
    msg: messageAddProperty,
  } = useSelector((state) => state.addProperty);

  useEffect(() => {
    if ((successUpdate || successDelete) && hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
    }
  }, [successUpdate, successDelete, dispatch, hostInfo?._id]);

  useEffect(() => {
    if (successUpdate || successDelete || successAddProperty) {
      setTimeout(() => {
        successUpdate && dispatch(updatePropertyReset());
        successDelete && dispatch(deletePropertyReset());
        successAddProperty && dispatch(addPropertyReset());
      }, 2000);
    }
  }, [successAddProperty, successUpdate, successDelete, dispatch]);

  const handleNext = () => setTutorialStep((prev) => prev + 1);
  const handleBack = () => setTutorialStep((prev) => prev - 1);
  const handleClose = () => setShowTutorial(false);

  let propertyList = Array.isArray(properties) ? properties : [];
  if (search.length !== 0) {
    propertyList = propertyList.filter((property) => {
      try {
        const addressString = `${property?.address?.line1 || ""} ${property?.address?.line2 || ""}, ${property?.address?.city || ""}, ${property?.address?.state || ""} ${property?.address?.zipCode || ""}, United States`
          .toLowerCase();
        return addressString.includes(search.toLowerCase());
      } catch (error) {
        console.error("Error filtering property:", error);
        return false;
      }
    });
  }

  // Calculate analytics with safe fallbacks
  const activeProperties = Array.isArray(properties) ? properties.filter(p => !p.isDeleted) : [];
  let totalEarnings = 0;
  let totalOrders = 0;
  let avgOrderValue = 0;

  if (earnings && typeof earnings === 'object') {
    try {
      for (const propertyId in earnings) {
        const propertyEarnings = earnings[propertyId]?.earnings || [];
        if (Array.isArray(propertyEarnings)) {
          const sum = propertyEarnings.reduce((acc, amount) => {
            const numAmount = typeof amount === 'number' ? amount : 0;
            return acc + numAmount;
          }, 0);
          totalEarnings += sum;
          totalOrders += propertyEarnings.length;
        }
      }
      avgOrderValue = totalOrders > 0 ? totalEarnings / totalOrders : 0;
    } catch (error) {
      console.error("Error calculating earnings:", error);
      totalEarnings = 0;
      totalOrders = 0;
      avgOrderValue = 0;
    }
  }

  if (loadingProperties || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#EF4949' }} />
          <p className="mt-4 text-gray-600 font-semibold">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (selfOnboarding && !isOnboardingBooked && !isOnBoarded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100">
        {showTutorial && (
          <TutorialModal
            step={tutorialStep}
            onNext={handleNext}
            onBack={handleBack}
            onClose={handleClose}
          />
        )}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <HostNav searchBar={true} setSearch={setSearch} />
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg p-8 text-center border border-white/40">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Schedule Your Onboarding Call</h2>
            <Calendly />
            <p className="text-gray-600 mt-6 max-w-2xl mx-auto">
              Once scheduled, our team will walk you through setup and answer any questions you have.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 relative overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-primary-500/5 to-purple-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-gradient-to-tr from-blue-500/5 to-primary-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <HostNav searchBar={true} setSearch={setSearch} />

        {/* Success/Error Messages */}
        {successAddProperty && (
          <Alert severity="success" className="mb-3">{messageAddProperty}</Alert>
        )}
        {successUpdate && (
          <Alert severity="success" className="mb-3">{messsageupdate}</Alert>
        )}
        {successDelete && (
          <Alert severity="warning" className="mb-3">{messageDelete}</Alert>
        )}

        {isOnBoarded === false ? (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-md p-6 border border-white/40">
            {errorOnBorad && (
              <Alert severity="error" className="mb-4">{errorOnBorad}</Alert>
            )}
            <div className="text-center max-w-md mx-auto">
              <div className="mb-4">
                <img src={onboard} alt="Onboarding" className="mx-auto h-20 w-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Complete Your Setup</h3>
              <p className="text-gray-600 mb-4">
                Cannot add properties until you have set up your Stripe Account
              </p>
              <button
                onClick={handleClick}
                disabled={onBoardLoading}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {onBoardLoading ? (
                  <>
                    <CircularProgress color="inherit" className="mr-2" size={20} />
                    Connecting...
                  </>
                ) : (
                  "Link Stripe Account"
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {error && <Alert severity="error">{error}</Alert>}

            {/* Compact Analytics Bar */}
            <AnalyticsBar
              activeProperties={activeProperties.length}
              totalEarnings={totalEarnings}
              totalOrders={totalOrders}
              avgOrderValue={avgOrderValue}
            />

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
              {/* Your Properties section - commented out
              <div className="xl:col-span-3">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900">Your Properties</h2>
                  <div className="flex items-center space-x-3">
                    <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
                    <Link
                      to="/add-property"
                      className="flex items-center space-x-1.5 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-md shadow-primary-500/25 hover:shadow-lg text-xs font-medium hover:-translate-y-0.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Add Property</span>
                    </Link>
                  </div>
                </div>

                {loadingProperties ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm p-6 animate-pulse border border-white/40">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    ))}
                  </div>
                ) : propertyList.length === 0 ? (
                  <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-12 text-center border border-white/40 shadow-sm">
                    <div className="max-w-md mx-auto">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary-500/25">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {search?.length > 0 ? "No Properties Found" : "Start by Adding Your First Property"}
                      </h3>
                      <p className="text-gray-500 text-sm mb-5">
                        {search?.length > 0
                          ? "Try adjusting your search terms"
                          : "Add your first property to get started with Bellhopt"
                        }
                      </p>
                      {search?.length === 0 && (
                        <Link
                          to="/add-property"
                          className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 font-medium text-sm"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add Property
                        </Link>
                      )}
                    </div>
                  </div>
                ) : viewMode === 'list' ? (
                  <div className="space-y-2">
                    {propertyList.map((property) => (
                      <PropertyListRow
                        key={property._id}
                        property={property}
                        data={data}
                        rawDataList={rawDataList}
                        earnings={earnings}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {propertyList.map((property) => (
                      <Property
                        key={property._id}
                        property={property}
                        data={data}
                        rawDataList={rawDataList}
                      />
                    ))}
                  </div>
                )}
              </div>
              */}

              {/* Sidebar - spans full width while Your Properties is commented out */}
              <div className="space-y-4 xl:col-span-4">
                <UpcomingOrdersWidget orders={allOrders} receiveEntryInstructionsEmails={receiveEntryInstructionsEmails} />
                <QuickActionsWidget />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostScreen;
